import styled from "styled-components";
import { useDispatch } from "react-redux";
import { noteSelectionCleared } from "../../store/notesSlice";
import SimpleMDE from "react-simplemde-editor";
import EasyMDE from "easymde";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styleSystem } from "../../../toolkit/styleSystem";
import { logInfo } from "../../../../utils/log";
import useNoteEditor from "./useNoteEditor";

const Wrapper = styled.div`
  height: 100%;
  line-height: 1.5;
  max-width: 100%;
  ${(p) => (!p.visible ? "visibility:hidden;" : "")}
  //overflow: scroll;
  ${styleSystem()}
  // Disable IOS zoom while editing.
  textarea {
    font-size: 16px;
  }

  // Make easymde height 100%
  & > div {
    height: 100%;
  }

  .EasyMDEContainer {
    height: 100%;
  }

  .CodeMirror {
    height: 100%;
  }
`;

const NoteEditorWindow = ({ getController, setHasUnsaved, ...props }) => {
  const dispatch = useDispatch();
  const [codemirrorInstance, setCodemirrorInstance] = useState(null);

  const [mde, setMde] = useState();

  const editor = useMemo(() => {
    const setEditorValue = (value) => mde.value(value);

    const getEditorValue = () => mde?.codemirror?.getTextArea()?.value;

    return { getEditorValue, setEditorValue };
  }, [mde]);

  const { performSave, onChange, revert, mobile, loaded } = useNoteEditor(
    mde && editor
  );

  const getCmInstanceCallback = useCallback((editor) => {
    setCodemirrorInstance(editor);
  }, []);

  useEffect(() => {
    if (codemirrorInstance) {
      logInfo("setting size");
      codemirrorInstance.setSize("100%", "100%");
    }
  }, [codemirrorInstance]);
  const codeTag = "```";
  // get controller which is used to execute commands from parent controls.
  // This is necessary to drive the toolbar etc.
  useEffect(() => {
    getController &&
      getController({
        toggleHeadingSmaller() {
          EasyMDE.toggleHeadingSmaller(mde);
        },
        toggleItalic() {
          EasyMDE.toggleItalic(mde);
        },
        toggleBold() {
          EasyMDE.toggleBold(mde);
        },
        toggleStrikethrough() {
          EasyMDE.toggleStrikethrough(mde);
        },
        toggleBlockquote() {
          EasyMDE.toggleBlockquote(mde);
        },
        toggleCodeBlock() {
          EasyMDE.toggleCodeBlock(mde);
        },
        task() {
          const cm = mde.codemirror;
          cm.replaceSelection(`${codeTag}task:${Date.now()}\n\n${codeTag}`);
        },
        image() {
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^<Image>(.*)<\/Image>$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`<Image>${text}</Image>`);
          }
        },
        video() {
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^<Video>(.*)<\/Video>$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`<Video>${text}</Video>`);
          }
        },
        pdf() {
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^<Pdf>(.*)<\/Pdf>$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`<Pdf>${text}</Pdf>`);
          }
        },
        mermaidDiagram() {
          const codeTag = "```";
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^```mermaid(.*)```$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`${codeTag}mermaid\n${text}\n${codeTag}`);
          }
        },
        graphviz() {
          const codeTag = "```";
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^```graphviz(.*)```$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`${codeTag}graphviz\n${text}\n${codeTag}`);
          }
        },
        json() {
          const codeTag = "```";
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^```json(.*)```$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`${codeTag}json\n${text}\n${codeTag}`);
          }
        },
        drawTable() {
          EasyMDE.drawTable(mde);
        },
        undo() {
          EasyMDE.undo(mde);
        },
        toggleUnderline() {
          const cm = mde.codemirror;
          const text = cm.getSelection();
          const match = /^<u>(.*)<\/u>$/is.exec(text);

          if (match) {
            cm.replaceSelection(`${match[1]}`);
          } else {
            cm.replaceSelection(`<u>${text}</u>`);
          }
        },
        redo() {
          EasyMDE.redo(mde);
        },
        drawHorizontalRule() {
          EasyMDE.drawHorizontalRule(mde);
        },
        toggleOrderedList() {
          EasyMDE.toggleOrderedList(mde);
        },

        toggleUnorderedList() {
          EasyMDE.toggleUnorderedList(mde);
        },
        revert,
        async save() {
          await performSave();
          if (mobile) {
            dispatch(noteSelectionCleared());
          }
        },
      });
  }, [mde, performSave, mobile, revert, getController, dispatch]);

  // We need to memo this because passing new ref object to mde
  // will reinitialise the control
  const options = useMemo(
    () => ({
      autofocus: false,
      status: false,
      toolbar: false,
      forceSync: false,
    }),
    []
  );

  const onGetMde = useCallback((mde) => {
    logInfo("setting mde. Has Value", !!mde);
    setMde(mde);
  }, []);

  //  if (!note) return null;
  return (
    <Wrapper visible={loaded} {...props}>
      <SimpleMDE
        getCodemirrorInstance={getCmInstanceCallback}
        getMdeInstance={onGetMde}
        options={options}
        spellCheck={true}
        onChange={onChange}
      />
    </Wrapper>
  );
};

export default NoteEditorWindow;
